import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { FxAlert, FxButton, FxIcon, FxPanel, FxSpinner, FxTextField } from '@/components/fx'
import { templateEditError } from '@/lib/api'
import { useBlobUrl } from '@/hooks/useBlobUrl'
import { cn } from '@/lib/utils'
import type { SinglePage } from '@/types/domain'

import { AddVariantDialog } from './AddVariantDialog'
import { ConfirmDialog, DialogShell } from './DialogShell'
import {
  documentsLabel,
  editorPath,
  layoutMeta,
  layoutName,
  type LayoutEntry,
} from './templateTabModel'
import { useConversionWatch } from './useConversionWatch'
import { editErrorMessage, type TemplateEdits } from './useTemplateEdits'

/**
 * "Layouts": the same form in its several fassungen, as cards.
 *
 * The one thing this section exists to make obvious is that a variant is not
 * a second form. Its fields are the same fields, its documents land in the
 * same export, and the only reason it exists at all is that the paper
 * changed. So the cards sit in one row with the primary layout, and the
 * field matrix below says which of the shared fields each of them carries.
 */
export function LayoutsPanel({
  templateId,
  entries,
  targets,
  canWrite,
  edits,
  onRefresh,
}: {
  templateId: string
  entries: LayoutEntry[]
  targets: SinglePage[]
  canWrite: boolean
  edits: TemplateEdits
  onRefresh: () => void
}) {
  const [adding, setAdding] = useState(false)
  const converting = entries.filter((entry) => entry.page.status === 'converting').map((e) => e.page.page)
  const { stalled, resume } = useConversionWatch(converting, onRefresh)

  return (
    <FxPanel
      title="Layouts"
      meta={
        <span className="text-small text-text-muted">
          Dasselbe Formular in mehreren Fassungen – die Felder bleiben dieselben Spalten im Export.
        </span>
      }
      className="p-5 xs:p-8"
    >
      {stalled && (
        <FxAlert tone="warning" title="Die Umwandlung dauert ungewöhnlich lange" className="mb-6">
          <p>
            Eine hochgeladene PDF-Seite wird seit mehreren Minuten vorbereitet. Wir haben aufgehört
            nachzufragen.
          </p>
          <FxButton variant="outlined" className="mt-4" onClick={resume}>
            Noch einmal nachsehen
          </FxButton>
        </FxAlert>
      )}

      <ul className="m-0 flex list-none flex-wrap gap-6 p-0">
        {entries.map((entry) => (
          <li key={entry.page.page}>
            <LayoutCard templateId={templateId} entry={entry} canWrite={canWrite} edits={edits} />
          </li>
        ))}

        {canWrite && (
          <li>
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex h-full min-h-[300px] w-[260px] max-w-full flex-col items-center justify-center gap-[10px] rounded-sm border-2 border-dashed border-primary-tonal bg-transparent px-4 text-body-strong text-primary-text"
            >
              <FxIcon name="add_photo_alternate" size={48} />
              Variante hinzufügen
              <span className="text-small font-normal text-text">Bild oder PDF einer anderen Fassung</span>
            </button>
          </li>
        )}
      </ul>

      <AddVariantDialog
        open={adding}
        onClose={() => setAdding(false)}
        targets={targets}
        edits={edits}
      />
    </FxPanel>
  )
}

function LayoutCard({
  templateId,
  entry,
  canWrite,
  edits,
}: {
  templateId: string
  entry: LayoutEntry
  canWrite: boolean
  edits: TemplateEdits
}) {
  const [dialog, setDialog] = useState<null | 'rename' | 'delete'>(null)
  const page = entry.page
  const name = layoutName(page)
  const ready = page.status === 'ready'
  const isVariant = page.variantOf != null

  return (
    <article className="flex w-[260px] max-w-full flex-col overflow-hidden rounded-sm bg-surface shadow-raised">
      <div className="flex h-[230px] items-center justify-center bg-fill px-4">
        <PagePreview page={page} />
      </div>

      <div className="flex flex-col gap-2 px-4 py-[14px]">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-card-name text-text" title={name}>
            {name}
          </span>
          <span
            className={cn(
              'rounded-sm px-2 py-[2px] text-caption',
              isVariant ? 'bg-primary-subtle text-primary-text' : 'bg-success-subtle text-success-on-subtle',
            )}
          >
            {isVariant ? 'Variante' : 'Standard'}
          </span>
        </div>

        <span className="text-small text-text">{layoutMeta(entry)}</span>

        <div className="flex gap-2">
          {ready ? (
            <FxButton variant="tonal" asChild className="h-9 flex-1">
              <Link to={editorPath(templateId, page.page)}>Bearbeiten</Link>
            </FxButton>
          ) : (
            <FxButton variant="tonal" disabled className="h-9 flex-1">
              Bearbeiten
            </FxButton>
          )}

          {canWrite && (
            <CardMenu
              name={name}
              removable={entry.removable}
              onRename={() => setDialog('rename')}
              onDelete={() => setDialog('delete')}
            />
          )}
        </div>
      </div>

      <RenameLayoutDialog
        open={dialog === 'rename'}
        onClose={() => setDialog(null)}
        page={page}
        edits={edits}
      />
      <DeleteLayoutDialog
        open={dialog === 'delete'}
        onClose={() => setDialog(null)}
        entry={entry}
        edits={edits}
      />
    </article>
  )
}

/**
 * The page image, or what is happening instead of one.
 *
 * `converting` and `failed` are states of a real page row, not of a request,
 * so they are drawn where the page would be rather than as an error strip
 * over the whole section: the other layouts are fine.
 */
function PagePreview({ page }: { page: SinglePage }) {
  const { url } = useBlobUrl(page.status === 'ready' ? page.imagePath : undefined)

  if (page.status === 'converting') {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <FxSpinner label="Seite wird vorbereitet …" />
        <span className="text-small text-text-muted">wird vorbereitet …</span>
      </div>
    )
  }

  if (page.status === 'failed') {
    return (
      <div className="flex flex-col items-center gap-2 px-2 text-center">
        <FxIcon name="error" size={28} className="text-danger-text" />
        <span className="text-small text-danger-text">Umwandlung fehlgeschlagen</span>
        <span className="text-caption text-text-muted">
          Entfernen Sie die Fassung und laden Sie sie noch einmal hoch.
        </span>
      </div>
    )
  }

  if (!url) {
    // The bytes are behind the session, so there is a fetch between having
    // the path and having a picture. An empty frame beats a broken image.
    return <span className="text-small text-text-muted">wird geladen …</span>
  }

  return <img src={url} alt="" className="max-h-[214px] w-auto max-w-full bg-surface object-contain shadow-raised" />
}

function CardMenu({
  name,
  removable,
  onRename,
  onDelete,
}: {
  name: string
  removable: boolean
  onRename: () => void
  onDelete: () => void
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Aktionen für ${name}`}
          className="inline-flex shrink-0 items-center rounded-sm p-1 text-text hover:text-primary-text"
        >
          <FxIcon name="more_vert" size={24} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 min-w-[220px] rounded-xl bg-surface p-2 text-body text-text shadow-overlay"
        >
          <DropdownMenu.Item
            onSelect={onRename}
            className="flex cursor-default items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-primary-subtle data-[highlighted]:text-primary-text"
          >
            <FxIcon name="edit" size={20} />
            Umbenennen
          </DropdownMenu.Item>
          {/* Page 1 and the last remaining page are refused by the gateway
              (400 `page_immutable`), so the item is not offered rather than
              offered and then refused. */}
          {removable && (
            <DropdownMenu.Item
              onSelect={onDelete}
              className="flex cursor-default items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-danger-subtle data-[highlighted]:text-danger-text"
            >
              <FxIcon name="delete" size={20} />
              Entfernen
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function RenameLayoutDialog({
  open,
  onClose,
  page,
  edits,
}: {
  open: boolean
  onClose: () => void
  page: SinglePage
  edits: TemplateEdits
}) {
  const [value, setValue] = useState(page.label ?? '')
  const { setPageLabel } = edits

  const save = () => {
    setPageLabel.mutate(
      { page: page.page, label: value.trim() },
      {
        onSuccess: () => {
          setPageLabel.reset()
          onClose()
        },
      },
    )
  }

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title="Fassung umbenennen"
      description={`Der Name steht auf der Karte und in der Spalte „Layout“ der Dokumentliste. Ohne Namen heißt die Fassung „Seite ${page.page}“.`}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
      >
        <FxTextField
          label="Bezeichnung"
          placeholder="z. B. 2018"
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          error={setPageLabel.isError ? editErrorMessage(setPageLabel.error) : undefined}
        />
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <FxButton type="button" variant="outlined" onClick={onClose}>
            Abbrechen
          </FxButton>
          <FxButton type="submit" disabled={setPageLabel.isPending}>
            Speichern
          </FxButton>
        </div>
      </form>
    </DialogShell>
  )
}

/**
 * Removing a layout.
 *
 * The gateway refuses (409 `page_in_use`) while documents are matched to the
 * page and reports how many; `?force=true` overrides. Those documents do not
 * disappear — they lose their layout and with it their analysis' frame of
 * reference — so the second question names the number and says what happens,
 * rather than repeating "sind Sie sicher?".
 */
function DeleteLayoutDialog({
  open,
  onClose,
  entry,
  edits,
}: {
  open: boolean
  onClose: () => void
  entry: LayoutEntry
  edits: TemplateEdits
}) {
  const { deletePage } = edits
  const name = layoutName(entry.page)
  const refusal = deletePage.isError ? templateEditError(deletePage.error) : null
  const inUse = refusal?.code === 'page_in_use'
  // The gateway's own count, which can differ from the card's: `byMatchedPage`
  // is a moment old and an analysis may have finished since.
  const documents = refusal?.documents ?? entry.documents

  const close = () => {
    deletePage.reset()
    onClose()
  }

  if (inUse) {
    return (
      <ConfirmDialog
        open={open}
        onClose={close}
        title="Diese Fassung wird noch verwendet"
        description={
          <p>
            {documentsLabel(documents)} {documents === 1 ? 'ist' : 'sind'} dieser Fassung
            zugeordnet. Beim Entfernen verlieren {documents === 1 ? 'es' : 'sie'} diese Zuordnung —
            die Dokumente selbst bleiben erhalten und können einer anderen Fassung zugeordnet
            werden.
          </p>
        }
        confirmLabel="Trotzdem entfernen"
        pending={deletePage.isPending}
        onConfirm={() =>
          deletePage.mutate({ page: entry.page.page, force: true }, { onSuccess: close })
        }
      />
    )
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={close}
      title="Fassung entfernen?"
      description={
        <p>
          „{name}“ wird aus der Vorlage entfernt, mit allen Feldern, die darauf platziert sind. Die
          gemeinsamen Felder bleiben bestehen.
        </p>
      }
      confirmLabel="Entfernen"
      pending={deletePage.isPending}
      error={deletePage.isError ? editErrorMessage(deletePage.error) : null}
      onConfirm={() => deletePage.mutate({ page: entry.page.page }, { onSuccess: close })}
    />
  )
}
